import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Link } from '../PageBuilder/Primitives/Link';
import { Modal } from '../../components';

import css from './LandingPage.module.css';

export const normalizeLandingPageHref = hrefValue => {
  if (typeof hrefValue !== 'string') {
    return hrefValue;
  }
  if (hrefValue.startsWith('/')) {
    return hrefValue;
  }

  try {
    const parsed = new URL(hrefValue);
    const isTaskOrListingCreatePath =
      parsed.pathname.startsWith('/l/new') || parsed.pathname.startsWith('/l/draft');
    return isTaskOrListingCreatePath
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : hrefValue;
  } catch (e) {
    return hrefValue;
  }
};

// The server renders every page signed out (it never loads the session), while the client knows
// `isAuthenticated` before hydration. Until the first client render has committed, the auth state
// is read as signed out so the hydrating render matches the server markup; later renders (and
// SPA navigations, once hydrated) read the real state — the TopbarDesktop pattern (backlog #4, H1-a).
let hasHydratedOnce = false;
const useIsAuthenticatedAfterHydration = () => {
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const [mounted, setMounted] = useState(hasHydratedOnce);
  useEffect(() => {
    hasHydratedOnce = true;
    setMounted(true);
  }, []);
  return mounted && isAuthenticated;
};

/**
 * The landing viewer's role: 'guest', 'provider', 'customer', or 'authenticated-unknown' (signed in,
 * userType not resolved yet). One source for role-dependent landing UI: the category directory
 * popover actions (D22) and the audience panel order (T14).
 */
export const useLandingViewerRole = () => {
  const isAuthenticated = useIsAuthenticatedAfterHydration();
  const currentUser = useSelector(state => state.user?.currentUser);
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  return !isAuthenticated
    ? 'guest'
    : userType === 'provider'
    ? 'provider'
    : userType === 'customer'
    ? 'customer'
    : 'authenticated-unknown';
};

/**
 * `rootClassName` replaces the PageBuilder Link's own `.link` class (text-link colour, display: inline)
 * for a link styled as a button. `.link` has the same specificity as a button class, and the search
 * page chunk loads it again later (NamedLink preloads it on hover / touch), so it would win (T20).
 */
export const LandingPageAuthAwareLink = props => {
  const { children, href, className: classNameProp, rootClassName, onCustomerServiceClick } = props;
  const className = rootClassName ? classNames(rootClassName, classNameProp) : classNameProp;
  const normalizedHref = normalizeLandingPageHref(href);
  const history = useHistory();
  const isAuthenticated = useIsAuthenticatedAfterHydration();
  const currentUser = useSelector(state => state.user?.currentUser);
  const userType = currentUser?.attributes?.profile?.publicData?.userType;

  const isGuest = !isAuthenticated;
  const isCustomer = userType === 'customer';
  const isProvider = userType === 'provider';

  const isTaskButton = typeof normalizedHref === 'string' && normalizedHref.includes('type=tasks');
  const isServiceButton =
    typeof normalizedHref === 'string' &&
    (normalizedHref.includes('l/new') || normalizedHref.includes('l/draft')) &&
    !isTaskButton;
  const isPublishButton = isTaskButton || isServiceButton;

  if (isGuest && (isTaskButton || isServiceButton)) {
    return (
      <button
        type="button"
        className={classNames(className, css.authAwareButtonReset)}
        onClick={() =>
          history.push('/signup', { authReason: isTaskButton ? 'guest-task' : 'guest-service' })
        }
      >
        {children}
      </button>
    );
  }

  if (isCustomer && isServiceButton) {
    return (
      <button
        type="button"
        className={classNames(className, css.authAwareButtonReset)}
        onClick={onCustomerServiceClick}
      >
        {children}
      </button>
    );
  }

  if (isProvider) {
    if (isPublishButton) {
      return (
        <a href={normalizedHref} className={className} target="_self">
          {children}
        </a>
      );
    }
    return (
      <Link href={normalizedHref} rootClassName={rootClassName} className={classNameProp}>
        {children}
      </Link>
    );
  }

  if (isPublishButton) {
    return (
      <a href={normalizedHref} className={className} target="_self">
        {children}
      </a>
    );
  }

  return (
    <Link href={normalizedHref} rootClassName={rootClassName} className={classNameProp}>
      {children}
    </Link>
  );
};

export const LandingPageRoleNoticeModal = props => {
  const { isOpen, onClose, onManageDisableScrolling } = props;
  const intl = useIntl();

  return (
    <Modal
      id="RoleNoticeModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      ariaLabel={intl.formatMessage({ id: 'Modal.noticeLabel' })}
      useCloseTag
    >
      <div className={css.modalContent}>
        <p className={css.modalText}>
          <FormattedMessage id="LandingPage.roleNoticeModal" />
        </p>
        <button type="button" className={css.modalButton} onClick={onClose}>
          <FormattedMessage id="AuthenticationPage.noticeOk" />
        </button>
      </div>
    </Modal>
  );
};
